/* PickInvoiceForPaymentDialog — choose which invoice to record a payment
 * against.
 *
 * Used by Patient Profile → Billing tab and Sidebar Billing tab's top-
 * level "Record payment" CTA. Surfaces every invoice that still has a
 * balance (sent / viewed / partly paid). One click hands the chosen
 * invoice off to the standalone RecordPaymentDialog so the doctor can
 * log a cash/UPI/card receipt without finding the invoice manually.
 */
import React, { useMemo } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Money, Receipt, ArrowRight, CurrencyInr } from "@phosphor-icons/react";


const STATUS_TONE = {
    sent:    { label: "Sent",        bg: "bg-primary/10 text-primary border-primary/20" },
    viewed:  { label: "Viewed",      bg: "bg-sky-50 text-sky-700 border-sky-200" },
    partial: { label: "Partly paid", bg: "bg-amber-50 text-amber-800 border-amber-200" },
};

function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(amount);
}


export default function PickInvoiceForPaymentDialog({
    open, invoices = [], patientName, onClose, onPick,
}) {
    /* Only surface invoices that can still receive a payment. Doctors
       can't pay against drafts (they're not issued yet) or already-
       paid / void / refunded / uncollectible bills. */
    const payable = useMemo(
        () => (invoices || []).filter(
            (inv) =>
                ["sent", "viewed", "partial"].includes(inv.status) &&
                (inv.balance || 0) > 0,
        ),
        [invoices],
    );

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="max-w-lg max-h-[80vh] overflow-y-auto p-0"
                data-testid="pick-invoice-payment-dialog"
            >
                <DialogHeader className="px-6 pt-6 pb-3">
                    <DialogTitle className="flex items-center gap-2">
                        <Money size={18} weight="duotone" className="text-emerald-600" />
                        Record payment
                    </DialogTitle>
                    <DialogDescription>
                        {payable.length === 0
                            ? `No open invoices to pay${patientName ? ` for ${patientName}` : ""}.`
                            : `Pick the invoice ${patientName ? `${patientName} is ` : "you're "}paying for.`}
                    </DialogDescription>
                </DialogHeader>

                <div className="px-3 pb-4">
                    {payable.length === 0 ? (
                        <div className="px-6 py-10 text-center" data-testid="pick-invoice-empty">
                            <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                                <CurrencyInr size={20} weight="duotone" className="text-muted-foreground" />
                            </div>
                            <p className="text-sm font-medium">All invoices are settled.</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Issue a new invoice first if you'd like to log a payment.
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onClose}
                                className="rounded-full mt-4"
                                data-testid="pick-invoice-close"
                            >
                                Close
                            </Button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-border/40" data-testid="pick-invoice-list">
                            {payable.map((inv) => (
                                <li key={inv.id}>
                                    <PickRow invoice={inv} onPick={() => onPick(inv)} />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}


function PickRow({ invoice, onPick }) {
    const tone = STATUS_TONE[invoice.status] || STATUS_TONE.sent;
    return (
        <button
            onClick={onPick}
            className="w-full text-left px-4 py-3 rounded-lg hover:bg-secondary/40 transition-colors flex items-center gap-3 group"
            data-testid={`pick-invoice-row-${invoice.id}`}
        >
            <div className="w-9 h-9 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                <Receipt size={15} weight="duotone" className="text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-semibold">{invoice.code}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${tone.bg}`}>
                        {tone.label}
                    </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {(invoice.line_items || []).map((li) => li.description).slice(0, 2).join(" · ") || "—"}
                </div>
            </div>
            <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Balance</div>
                <div className="font-display text-base font-medium tabular-nums text-rose-700">
                    {formatINR(invoice.balance)}
                </div>
            </div>
            <ArrowRight
                size={14}
                className="text-muted-foreground/40 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition-all"
            />
        </button>
    );
}
